import { unwrap } from "@sagot/api-client";
import { API_KEY_IP_RANGES_MAX, API_SIGNATURE_HEADER, APP_URLS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Button, Checkbox, Dialog, DialogContent, Icon, ListEditor, toast, type ListEditorLabels, type ListEntryResult } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useApi } from "../../../../session/session-provider";
import type { ApiKeyOut } from "../../shared";
import { SecretOnce } from "./panel-kit";

const K = "settings.api";
const IPV4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
const IPV6_CHARS = /^[0-9a-f:.]+$/i;
const PREFIX = /^\d{1,3}$/;
const IPV4_MAX_PREFIX = 32;
const IPV6_MAX_PREFIX = 128;
const OCTET_MAX = 255;

function isIpv6(address: string): boolean {
  if (!address.includes(":") || !IPV6_CHARS.test(address)) return false;
  try {
    return new URL(`http://[${address}]/`).hostname.length > 0;
  } catch {
    return false;
  }
}

/** A typed address or range in CIDR form (a bare address gets /32 or /128), or `null` when it is not an IPv4/IPv6 address or range. The API normalises it again (host bits cleared). */
export function parseIpRange(raw: string): string | null {
  const text = raw.trim();
  const slash = text.indexOf("/");
  const address = slash === -1 ? text : text.slice(0, slash);
  const prefixText = slash === -1 ? null : text.slice(slash + 1);
  if (prefixText !== null && !PREFIX.test(prefixText)) return null;
  const prefix = prefixText === null ? null : Number(prefixText);
  const octets = IPV4.exec(address)?.slice(1);
  if (octets) {
    const bad = octets.some((octet) => Number(octet) > OCTET_MAX || (octet.length > 1 && octet.startsWith("0")));
    if (bad || (prefix !== null && prefix > IPV4_MAX_PREFIX)) return null;
    return `${address}/${prefix ?? IPV4_MAX_PREFIX}`;
  }
  if (!isIpv6(address) || (prefix !== null && prefix > IPV6_MAX_PREFIX)) return null;
  return `${address.toLowerCase()}/${prefix ?? IPV6_MAX_PREFIX}`;
}

interface IpRangesFieldProps {
  ranges: readonly string[];
  onChange: (ranges: string[]) => void;
  label: string;
  hint?: string;
  note?: string;
}

/** The allowlist editor (also inside the create-key dialog): one validated range per entry, at most 20. */
export function IpRangesField({ ranges, onChange, label, hint, note }: IpRangesFieldProps) {
  const { t } = useTranslation();
  const labels: ListEditorLabels = {
    add: t(`${K}.allowlist.add`),
    placeholder: t(`${K}.allowlist.placeholder`),
    empty: t(`${K}.allowlist.empty`),
    count: (count, max) => t(`${K}.allowlist.count`, { count, max }),
    remove: (item) => t(`${K}.allowlist.remove`, { item }),
    duplicate: (item) => t(`${K}.allowlist.duplicate`, { item }),
    full: t(`${K}.allowlist.full`, { max: API_KEY_IP_RANGES_MAX }),
    added: (item) => t(`${K}.allowlist.added`, { item }),
    removed: (item) => t(`${K}.allowlist.removed`, { item }),
  };
  const parse = (raw: string): ListEntryResult => {
    const value = parseIpRange(raw);
    return value ? { ok: true, value } : { ok: false, error: t(`${K}.allowlist.invalid`) };
  };
  return <ListEditor label={label} hint={hint} note={note} items={ranges} onChange={onChange} parse={parse} max={API_KEY_IP_RANGES_MAX} labels={labels} icon="shield-check" />;
}

type SigningState = "off" | "optional" | "required";

function signingStateOf(key: Pick<ApiKeyOut, "signing_configured" | "signing_required">): SigningState {
  if (!key.signing_configured) return "off";
  return key.signing_required ? "required" : "optional";
}

const SIGNING_TONE = { off: "neutral", optional: "brand", required: "success" } as const;

interface SigningSectionProps {
  apiKey: ApiKeyOut;
  onChanged: () => Promise<unknown>;
  onIssued: (secret: string) => void;
}

/** Request signing: turn on (secret shown once), replace the secret, or turn off. */
function SigningSection({ apiKey, onChanged, onIssued }: SigningSectionProps) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const [state, setState] = useState<SigningState>(signingStateOf(apiKey));
  const [mustSign, setMustSign] = useState(state === "off" ? true : state === "required");
  const params = { path: { key_id: apiKey.id } };
  const issue = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/api-keys/{key_id}/signing-secret", { params, body: { required: mustSign } })),
    onSuccess: async (data) => {
      setState(data.required ? "required" : "optional");
      onIssued(data.signing_secret);
      await onChanged();
    },
    onError: (caught) => toast.error(message(caught)),
  });
  const turnOff = useMutation({
    mutationFn: () => unwrap(api.DELETE("/v1/api-keys/{key_id}/signing-secret", { params })),
    onSuccess: async () => {
      setState("off");
      toast.success(t(`${K}.signing.offToast`));
      await onChanged();
    },
    onError: (caught) => toast.error(message(caught)),
  });
  return (
    <section aria-labelledby="signing-title" className="space-y-3" data-testid="signing-section">
      <div className="flex flex-wrap items-center gap-2">
        <h3 id="signing-title" className="text-sm font-semibold">
          {t(`${K}.signing.title`)}
        </h3>
        <Badge tone={SIGNING_TONE[state]} data-testid="signing-state">
          {t(`${K}.signing.${state}`)}
        </Badge>
      </div>
      <p className="text-xs text-fg-muted">
        {t(`${K}.signing.body`, { header: API_SIGNATURE_HEADER })}{" "}
        <AppLink href={APP_URLS.docsApi} className="font-semibold text-primary-ink hover:underline" target="_blank" rel="noreferrer">
          {t(`${K}.signing.docs`)}
        </AppLink>
      </p>
      <Checkbox checked={mustSign} onCheckedChange={(checked) => { setMustSign(checked === true); }} label={t(`${K}.signing.require`)} />
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" size="sm" loading={issue.isPending} disabled={turnOff.isPending} onClick={() => { issue.mutate(); }}>
          <Icon name="refresh-cw" />
          {state === "off" ? t(`${K}.signing.turnOn`) : t(`${K}.signing.reissue`)}
        </Button>
        {state === "off" ? null : (
          <Button variant="ghost" size="sm" className="text-danger" loading={turnOff.isPending} disabled={issue.isPending} onClick={() => { turnOff.mutate(); }}>
            {t(`${K}.signing.turnOff`)}
          </Button>
        )}
      </div>
    </section>
  );
}

interface KeyAllowlistDialogProps {
  apiKey: ApiKeyOut;
  onClose: () => void;
  onChanged: () => Promise<unknown>;
}

/** Per-key IP allowlist editor plus request signing; a new signing secret is shown once on a second screen. */
export function KeyAllowlistDialog({ apiKey, onClose, onChanged }: KeyAllowlistDialogProps) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const [ranges, setRanges] = useState<string[]>([...apiKey.allowed_ip_ranges]);
  const [secret, setSecret] = useState<string | null>(null);
  const changed = ranges.join("\n") !== apiKey.allowed_ip_ranges.join("\n");
  const save = useMutation({
    mutationFn: () => unwrap(api.PUT("/v1/api-keys/{key_id}/allowlist", { params: { path: { key_id: apiKey.id } }, body: { allowed_ip_ranges: ranges } })),
    onSuccess: async () => {
      toast.success(t(`${K}.allowlist.saved`));
      await onChanged();
      onClose();
    },
    onError: (caught) => toast.error(message(caught)),
  });
  const close = (open: boolean) => {
    if (!open) onClose();
  };

  if (secret) {
    return (
      <Dialog open onOpenChange={close}>
        <DialogContent title={t(`${K}.signing.issuedTitle`)} closeLabel={t("common.close")} footer={<Button onClick={onClose}>{t(`${K}.signing.saved`)}</Button>}>
          <SecretOnce value={secret} label={t(`${K}.signing.secretLabel`)} warning={<><strong>{t(`${K}.signing.warningStrong`)}</strong> {t(`${K}.signing.warning`)}</>} />
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <Dialog open onOpenChange={close}>
      <DialogContent
        title={t(`${K}.allowlist.title`)}
        description={t(`${K}.allowlist.description`, { name: apiKey.name })}
        closeLabel={t("common.close")}
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button loading={save.isPending} disabled={!changed} onClick={() => { save.mutate(); }}>
              {t(`${K}.allowlist.save`)}
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          <IpRangesField ranges={ranges} onChange={setRanges} label={t(`${K}.allowlist.sectionIps`)} hint={t(`${K}.allowlist.hint`)} note={t(`${K}.allowlist.note`)} />
          <hr className="border-line" />
          <SigningSection apiKey={apiKey} onChanged={onChanged} onIssued={setSecret} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
